'use client';

import { uniq } from 'es-toolkit';
import Image from 'next/image';
import type React from 'react';
import type { ComponentType, ElementType, JSX, ReactElement } from 'react';
import { useMemo } from 'react';
import type { Components, ExtraProps } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as RehypeSanitizeOptions } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
type MarkdownComponentProperties<K extends keyof Components> = Components[K] extends ComponentType<infer P> ? P : JSX.IntrinsicElements[K] & ExtraProps;
type RenderAnchor = (overrides?: {
  target?: string | undefined;
  rel?: string | undefined;
}) => ReactElement<unknown, ElementType>;
export type StreamdownAnchorTransform = (context: {
  url: string;
  anchorChildren: React.ReactNode;
  renderAnchor: RenderAnchor;
}) => React.ReactNode;
interface StreamdownRendererProperties {
  children: string;
  className?: string | undefined;
  components?: Components | undefined;
  transformAnchor?: StreamdownAnchorTransform | undefined;
}
const DEFAULT_IMAGE_WIDTH = 1024;
const DEFAULT_IMAGE_HEIGHT = 576;
const parseNumericDimension = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
const defaultAttributes = defaultSchema.attributes ?? {};
const augmentAttributes = (values: unknown, extras: readonly string[]): string[] => {
  const base = Array.isArray(values) && values.length > 0 ? values.filter((value): value is string => typeof value === 'string') : [];
  return uniq([...base, ...extras]);
};
type AttributeKey = '*' | 'a' | 'code' | 'span' | 'img';
const attributesFor = (key: AttributeKey): string[] => {
  const candidate = defaultAttributes[key];
  return Array.isArray(candidate) ? candidate.filter((value): value is string => typeof value === 'string') : [];
};
const sanitizeSchema: RehypeSanitizeOptions = {
  ...defaultSchema,
  attributes: {
    ...defaultAttributes,
    '*': augmentAttributes(attributesFor('*'), ['className']),
    a: augmentAttributes(attributesFor('a'), ['className', 'rel', 'target', 'data-preview', 'data-username', 'data-tweet-id']),
    code: augmentAttributes(attributesFor('code'), ['className']),
    span: augmentAttributes(attributesFor('span'), ['className']),
    img: augmentAttributes(attributesFor('img'), ['className', 'src', 'alt', 'title', 'width', 'height', 'loading'])
  }
};
const existingProtocols = (sanitizeSchema.protocols ?? {}) as {
  href?: unknown;
  src?: unknown;
};
const hrefProtocolsCandidate = existingProtocols.href ?? [];
const hrefProtocols = Array.isArray(hrefProtocolsCandidate) ? hrefProtocolsCandidate.filter((value): value is string => typeof value === 'string') : [];
const sourceProtocolsCandidate = existingProtocols.src ?? [];
const sourceProtocols = Array.isArray(sourceProtocolsCandidate) ? sourceProtocolsCandidate.filter((value): value is string => typeof value === 'string') : [];
sanitizeSchema.protocols = {
  ...existingProtocols,
  href: uniq([...hrefProtocols, 'suggest', 'tweet']),
  src: uniq([...sourceProtocols, 'http', 'https'])
};
const SAFE_IMAGE_PATTERN = /^https?:\/\//i;
export function StreamdownRenderer({
  children,
  className,
  components,
  transformAnchor
}: StreamdownRendererProperties) {
  const rehypePlugins = useMemo<NonNullable<React.ComponentProps<typeof ReactMarkdown>['rehypePlugins']>>(() => [rehypeRaw, [rehypeSanitize, sanitizeSchema] as [typeof rehypeSanitize, RehypeSanitizeOptions]], []);
  const baseComponents = useMemo<Components>(() => {
    const paragraph: NonNullable<Components['p']> = ({
      children: paragraphChildren,
      ...properties
    }: MarkdownComponentProperties<'p'>) => <p className="mb-2 break-words last:mb-0" {...properties}>
        {paragraphChildren}
      </p>;
    const unorderedList: NonNullable<Components['ul']> = ({
      children: unorderedListChildren,
      ...properties_0
    }: MarkdownComponentProperties<'ul'>) => <ul className="mb-2 list-disc break-words pl-4" {...properties_0}>
        {unorderedListChildren}
      </ul>;
    const orderedList: NonNullable<Components['ol']> = ({
      children: orderedListChildren,
      ...properties_1
    }: MarkdownComponentProperties<'ol'>) => <ol className="mb-2 list-decimal break-words pl-4" {...properties_1}>
        {orderedListChildren}
      </ol>;
    const listItem: NonNullable<Components['li']> = ({
      children: listItemChildren,
      ...properties_2
    }: MarkdownComponentProperties<'li'>) => <li className="mb-1 break-words" {...properties_2}>
        {listItemChildren}
      </li>;
    const headingOne: NonNullable<Components['h1']> = ({
      children: headingOneChildren,
      ...properties_3
    }: MarkdownComponentProperties<'h1'>) => <h1 className="mb-2 break-words text-xl font-bold" {...properties_3}>
        {headingOneChildren}
      </h1>;
    const headingTwo: NonNullable<Components['h2']> = ({
      children: headingTwoChildren,
      ...properties_4
    }: MarkdownComponentProperties<'h2'>) => <h2 className="mb-2 break-words text-lg font-bold" {...properties_4}>
        {headingTwoChildren}
      </h2>;
    const headingThree: NonNullable<Components['h3']> = ({
      children: headingThreeChildren,
      ...properties_5
    }: MarkdownComponentProperties<'h3'>) => <h3 className="mb-2 break-words text-base font-bold" {...properties_5}>
        {headingThreeChildren}
      </h3>;
    const anchor: NonNullable<Components['a']> = ({
      node,
      children: anchorChildren,
      href,
      ...properties_6
    }: MarkdownComponentProperties<'a'>) => {
      // Sanitizers occasionally blank out the `href` when custom schemes are used, so we fall back to the raw node
      // property to give downstream transforms a chance to recover the original value.
      const nodeProperties = (node?.properties ?? {}) as {
        href?: unknown;
      };
      const rawHref = typeof nodeProperties.href === 'string' ? nodeProperties.href : undefined;
      const url = typeof href === 'string' && href.length > 0 ? href : rawHref ?? '';
      const {
        className: incomingClassName,
        target,
        rel,
        ...rest
      } = properties_6 as MarkdownComponentProperties<'a'> & {
        className?: string;
        target?: string;
        rel?: string;
      };
      const anchorClassName = cn('font-medium text-[var(--mix-hslprimary-72pct-hslforeground-28pct)] hover:text-[var(--mix-hslprimary-62pct-hslforeground-38pct)] dark:text-[var(--mix-hslprimary-58pct-hslforeground-42pct)] dark:hover:text-[var(--mix-hslprimary-48pct-hslforeground-52pct)] underline underline-offset-2 decoration-[var(--mix-hslprimary-42pct-transparent-58pct)] hover:decoration-[var(--mix-hslprimary-56pct-transparent-44pct)] dark:decoration-[var(--mix-hslprimary-36pct-transparent-64pct)] dark:hover:decoration-[var(--mix-hslprimary-48pct-transparent-52pct)] transition-colors break-words', incomingClassName);
      const renderAnchor: RenderAnchor = overrides => <a href={url} target={overrides?.target ?? (typeof target === 'string' ? target : undefined)} rel={overrides?.rel ?? (typeof rel === 'string' ? rel : undefined)} className={anchorClassName} {...rest}>
          {anchorChildren}
        </a>;
      if (!url) {
        return renderAnchor();
      }
      if (transformAnchor) {
        const transformed = transformAnchor({
          url,
          anchorChildren,
          renderAnchor
        });
        if (transformed) {
          return <>{transformed}</>;
        }
      }
      return renderAnchor();
    };
    type CodeRendererProperties = MarkdownComponentProperties<'code'> & {
      inline?: boolean;
    };
    const code: NonNullable<Components['code']> = rawProperties => {
      const {
        inline,
        className: rawClassName,
        children: codeChildren,
        ...rest_0
      } = rawProperties as CodeRendererProperties;
      const classNames = typeof rawClassName === 'string' ? rawClassName : '';
      const isInline = inline ?? !(classNames.includes('language-') || classNames.includes('pre'));
      if (isInline) {
        return <code className={cn('rounded bg-muted px-1 py-0.5', classNames)} {...rest_0}>
            {codeChildren}
          </code>;
      }
      return <code className={cn('block overflow-x-auto whitespace-pre rounded bg-muted p-2', classNames)} {...rest_0}>
          {codeChildren}
        </code>;
    };
    const blockquote: NonNullable<Components['blockquote']> = ({
      children: blockquoteChildren,
      ...properties_7
    }: MarkdownComponentProperties<'blockquote'>) => <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic break-words" {...properties_7}>
        {blockquoteChildren}
      </blockquote>;
    const image: NonNullable<Components['img']> = ({
      src,
      alt,
      ...properties_8
    }: MarkdownComponentProperties<'img'>) => {
      if (typeof src !== 'string') {
        return null;
      }
      const normalized = src.trim();
      if (!SAFE_IMAGE_PATTERN.test(normalized)) {
        return null;
      }
      const {
        width,
        height,
        ...rest_1
      } = properties_8;
      const widthValue = parseNumericDimension(width);
      const heightValue = parseNumericDimension(height);
      const resolvedWidth = widthValue ?? DEFAULT_IMAGE_WIDTH;
      const resolvedHeight = heightValue ?? Math.max(1, Math.round(resolvedWidth / DEFAULT_IMAGE_WIDTH * DEFAULT_IMAGE_HEIGHT));
      return <div className="my-4 flex w-full justify-center">
          <Image src={normalized} alt={typeof alt === 'string' ? alt : ''} width={resolvedWidth} height={resolvedHeight} sizes="(min-width: 768px) 700px, 100vw" className="h-auto w-full max-w-full rounded-md border border-border/40 object-contain" loading="lazy" unoptimized {...rest_1} />
        </div>;
    };
    const componentsMap: Components = {
      p: paragraph,
      ul: unorderedList,
      ol: orderedList,
      li: listItem,
      h1: headingOne,
      h2: headingTwo,
      h3: headingThree,
      a: anchor,
      code,
      blockquote,
      img: image
    };
    return componentsMap;
  }, [transformAnchor]);
  const mergedComponents = useMemo<Components>(() => {
    if (!components) return baseComponents;
    return {
      ...baseComponents,
      ...components
    };
  }, [baseComponents, components]);
  return <div className={cn('streamdown-content break-words', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins} components={mergedComponents}>
        {children}
      </ReactMarkdown>
    </div>;
}